import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { ConsentBanner, useConsentStatus } from "../components/ConsentBanner";
import AIAnalysisLoader from "../components/AIAnalysisLoader";
import { devError } from "../utils/devLogger";

const LABEL_KEY_MAP = {
  "Very Low": "veryLow", "Low": "low", "Neutral": "neutral", "Good": "good", "Excellent": "excellent",
  "Exhausted": "exhausted", "Tired": "tired", "Moderate": "moderate", "Energetic": "energetic", "Full of Energy": "fullEnergy",
  "Very Stressed": "veryStressed", "Stressed": "stressed", "Calm": "calm", "Very Calm": "veryCalm",
  "Very Poor": "veryPoor", "Poor": "poor", "Average": "average",
  "Very Distracted": "veryDistracted", "Distracted": "distracted", "Focused": "focused", "Laser Focused": "laserFocused",
  "Not at All": "notAtAll", "Slightly": "slightly", "Moderately": "moderately", "Very": "very", "Extremely": "extremely",
  "None": "none", "Mild": "mild", "Significant": "significant", "Severe": "severe",
};

const Questionnaire = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAILoader, setShowAILoader] = useState(false);
  const [apiDone, setApiDone] = useState(false);
  const { hasConsent, consentType, checkConsent, markUsed } = useConsentStatus();
  const [showConsentBanner, setShowConsentBanner] = useState(false);

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/questionnaire/questions`);
      setQuestions(response.data.questions);
      
      const initialAnswers = {};
      response.data.questions.forEach(q => {
        if (q.type === 'slider') {
          initialAnswers[q.id] = 5;
        } else if (q.type === 'choice') {
          initialAnswers[q.id] = q.options[0];
        }
      });
      setAnswers(initialAnswers);
    } catch (error) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleSliderChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value[0] }));
  };

  const handleChoiceChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const runSubmission = async () => {
    setShowAILoader(true);
    setApiDone(false);
    try {
      const submitData = {
        responses: answers,
        mood_score: answers.mood || 5,
        energy_level: answers.energy || 5,
        stress_level: answers.stress || 5,
        sleep_quality: answers.sleep || 5,
        focus_level: answers.focus || 5,
        motivation_level: answers.motivation || 5
      };

      await axios.post(`${API}/questionnaire/submit`, submitData);

      if (consentType === "once") {
        await markUsed();
      }

      try {
        await axios.post(`${API}/training/generate`);
      } catch (planError) {
        devError("Plan generation error:", planError);
      }

      setApiDone(true);
    } catch (error) {
      toast.error("Failed to submit questionnaire");
      devError(error);
      setShowAILoader(false);
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!hasConsent) {
      setShowConsentBanner(true);
      return;
    }
    setSubmitting(true);
    runSubmission();
  };

  const handleConsentGranted = (type) => {
    setShowConsentBanner(false);
    checkConsent();
    setSubmitting(true);
    runSubmission();
  };

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="questionnaire-page">
      <AIAnalysisLoader
        isActive={showAILoader}
        apiDone={apiDone}
        onComplete={() => {
          toast.success(t('notifications.planReady'));
          navigate('/training');
        }}
      />
      {showConsentBanner && <ConsentBanner onConsented={handleConsentGranted} />}
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-sky-300/20 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-72 h-72 bg-indigo-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('common.dashboard')}
            </Button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">{t('questionnaire.title')}</span>
            </div>
          </div>
          <div className="mt-4 h-1 bg-sky-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-sky-400 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-sky-500 mt-2 text-center">
            {t('questionnaire.questionOf', { current: currentStep + 1, total: questions.length })}
          </p>
        </div>
      </header>

      <main className="pt-32 pb-24 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          {currentQuestion && (
            <div 
              key={currentQuestion.id}
              className="glass-card p-8 md:p-12 animate-fade-in"
              data-testid={`question-${currentQuestion.id}`}
            >
              <div className="mb-6">
                <span className={`
                  inline-block px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider
                  ${currentQuestion.category === 'cognitive' ? 'bg-indigo-100 text-indigo-600' : ''}
                  ${currentQuestion.category === 'physical' ? 'bg-cyan-100 text-cyan-600' : ''}
                  ${currentQuestion.category === 'resilience' ? 'bg-sky-100 text-sky-600' : ''}
                  ${currentQuestion.category === 'mood' ? 'bg-amber-100 text-amber-600' : ''}
                `}>
                  {t(`questionnaire.categories.${currentQuestion.category}`, currentQuestion.category)}
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-light text-sky-900 mb-8 leading-relaxed" style={{ fontFamily: 'Outfit' }}>
                {t(`questionnaire.questions.${currentQuestion.id}`, currentQuestion.question)}
              </h2>

              {currentQuestion.type === 'slider' && (
                <div className="space-y-8">
                  <div className="text-center">
                    <span className="text-5xl font-light text-sky-500" style={{ fontFamily: 'Outfit' }}>
                      {answers[currentQuestion.id]}
                    </span>
                    <span className="text-xl text-sky-300">/10</span>
                  </div>
                  <Slider
                    value={[answers[currentQuestion.id] || 5]}
                    onValueChange={(value) => handleSliderChange(currentQuestion.id, value)}
                    max={10}
                    min={1}
                    step={1}
                    className="w-full"
                    data-testid={`slider-${currentQuestion.id}`}
                  />
                  <div className="flex justify-between text-sm text-slate-500">
                    {currentQuestion.labels && currentQuestion.labels.map((label, idx) => {
                      const key = LABEL_KEY_MAP[label];
                      return (
                        <span key={key || idx} className={idx === 2 ? 'hidden sm:block' : ''}>
                          {key ? t(`questionnaire.labels.${key}`, label) : label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {currentQuestion.type === 'choice' && (
                <RadioGroup
                  value={answers[currentQuestion.id]}
                  onValueChange={(value) => handleChoiceChange(currentQuestion.id, value)}
                  className="space-y-3"
                  data-testid={`choice-${currentQuestion.id}`}
                >
                  {currentQuestion.options.map((option) => {
                    const key = LABEL_KEY_MAP[option];
                    const translatedLabel = key ? t(`questionnaire.labels.${key}`, option) : option;
                    return (
                      <div key={option} className="flex items-center">
                        <RadioGroupItem 
                          value={option} 
                          id={`${currentQuestion.id}-${option}`}
                          className="border-sky-300 text-sky-500"
                        />
                        <Label 
                          htmlFor={`${currentQuestion.id}-${option}`}
                          className="ml-3 text-lg text-sky-800 cursor-pointer"
                        >
                          {translatedLabel}
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
              )}
            </div>
          )}

          <div className="flex justify-between mt-8">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="text-sky-600 hover:text-sky-800 disabled:opacity-50"
              data-testid="prev-question-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('questionnaire.previous')}
            </Button>

            {currentStep === questions.length - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="glow-button"
                data-testid="submit-questionnaire-btn"
              >
                {submitting ? (
                  <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    {t('questionnaire.completeCheckin')}
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className="glow-button"
                data-testid="next-question-btn"
              >
                {t('common.next')}
                <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </div>
      </main>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Questionnaire;
